import { useState, useEffect, useRef } from 'react';
import {
  Briefcase, FileText, Zap, Activity, Send, CheckCircle,
  Loader2, X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { clientService } from '@core/supabase/services/clientService';
import { jobService } from '@core/supabase/services/jobService';
import { requisitionService } from '@core/supabase/services/requisitionService';
import { skillTaxonomyService, SkillSuggestion } from '@core/supabase/services/skillTaxonomyService';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { JOB_OPS_FLOW_STEPS, JOB_PUBLISH_FLOW_STEPS } from '@core/constants/jobLifecycle';
import type { Client, Job, Requisition } from '@core/types';
import { ClientOnboardingWizardStepper } from '../clients/ClientOnboardingWizardStepper';
import { JobOpsFlowStepper } from './JobOpsFlowStepper';
import { JobPublishFlowStepper } from './JobPublishFlowStepper';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';

const WIZARD_HIDE_KEY = 'ggj_b2b_hide_new_job_wizard';
const TOTAL = 6;
const STEP_LABELS = ['Basics', 'Description', 'Skills', 'Ops flow', 'Publish', 'Review'] as const;

const STEP_META: Record<number, { icon: React.ReactNode; title: string; subtitle: string }> = {
  1: { icon: <Briefcase className="w-4 h-4" />, title: 'Role basics', subtitle: 'Client, title, location and experience range.' },
  2: { icon: <FileText className="w-4 h-4" />, title: 'Job description', subtitle: 'Paste or link the JD — AI can draft it for you.' },
  3: { icon: <Zap className="w-4 h-4" />, title: 'Required skills', subtitle: 'Tag skills for auto-matching and search.' },
  4: { icon: <Activity className="w-4 h-4" />, title: 'Ops lifecycle', subtitle: 'Pipeline stages this role will move through.' },
  5: { icon: <Send className="w-4 h-4" />, title: 'Publish setup', subtitle: 'Careers site visibility and initial status.' },
  6: { icon: <CheckCircle className="w-4 h-4" />, title: 'Review & create', subtitle: 'Confirm all details before saving.' },
};

type FieldErrors = Record<string, string>;

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="text-xs text-red-500 mt-1 font-medium">{msg}</p>;
}
function Req() { return <span className="text-red-500 ml-0.5 font-bold">*</span>; }
function errCls(field: string, fe: FieldErrors) {
  return fe[field] ? 'border-red-500 ring-1 ring-red-500/40' : '';
}

export function JobNewWizardModal({
  open,
  onClose,
  onCreated,
  orgId,
  userId,
  initialClientId,
  prefillRequisition,
  requisitionId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (job: Job) => void;
  orgId: string;
  userId: string;
  initialClientId?: string;
  prefillRequisition: Requisition | null;
  requisitionId?: string;
}) {
  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [clients, setClients] = useState<Client[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [jdAiOpen, setJdAiOpen] = useState(false);

  const [draft, setDraft] = useState({
    client_id: initialClientId ?? '',
    title: '',
    location: '',
    experience_min: '',
    experience_max: '',
    jd_url: '',
    description: '',
    skills_required: [] as string[],
    post_publish: 'draft' as 'draft' | 'pending_review',
  });

  const [skillInput, setSkillInput] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState<SkillSuggestion[]>([]);
  const [showSkillDrop, setShowSkillDrop] = useState(false);
  const skillTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const set = (key: keyof typeof draft, value: unknown) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFieldErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  };

  /* reset wizard state when modal opens */
  useEffect(() => {
    if (open) { setStep(1); setMaxReached(1); setFieldErrors({}); }
  }, [open]);

  useEffect(() => {
    if (!orgId) return;
    clientService.list(orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
  }, [orgId]);

  useEffect(() => {
    if (!prefillRequisition) return;
    setDraft((d) => ({
      ...d,
      client_id: prefillRequisition.client_id || d.client_id,
      title: prefillRequisition.title?.trim() || d.title,
      description: prefillRequisition.business_justification?.trim() || d.description,
      location: prefillRequisition.location?.trim() || d.location,
      skills_required: prefillRequisition.must_have_skills?.length > 0 ? [...prefillRequisition.must_have_skills] : d.skills_required,
    }));
  }, [prefillRequisition?.id]);

  useEffect(() => {
    if (initialClientId) setDraft((d) => d.client_id ? d : { ...d, client_id: initialClientId });
  }, [initialClientId]);

  const goStep = (n: number) => {
    setStep(n);
    setMaxReached((m) => Math.max(m, n));
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };

  const validateStep1 = () => {
    const errs: FieldErrors = {};
    if (!draft.title.trim()) errs.title = 'Job title is required.';
    if (!draft.client_id) errs.client_id = 'Please select a client.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (step === 1 && !validateStep1()) return;
    if (step < TOTAL) goStep(step + 1);
  };

  const back = () => { setFieldErrors({}); if (step > 1) goStep(step - 1); };

  const handleSkillInput = (val: string) => {
    setSkillInput(val);
    if (skillTimer.current) clearTimeout(skillTimer.current);
    if (!val.trim()) { setSkillSuggestions([]); setShowSkillDrop(false); return; }
    skillTimer.current = setTimeout(async () => {
      const r = await skillTaxonomyService.search(orgId, val);
      setSkillSuggestions(r); setShowSkillDrop(r.length > 0);
    }, 200);
  };

  const addSkill = (override?: string) => {
    const s = (override ?? skillInput).trim();
    if (s && !draft.skills_required.includes(s))
      setDraft((d) => ({ ...d, skills_required: [...d.skills_required, s] }));
    setSkillInput(''); setSkillSuggestions([]); setShowSkillDrop(false);
  };
  const removeSkill = (s: string) => setDraft((d) => ({ ...d, skills_required: d.skills_required.filter((x) => x !== s) }));

  const submitJob = async () => {
    if (!validateStep1()) { goStep(1); return; }
    const selectedClient = clients.find((c) => c.id === draft.client_id);
    if (!isClientReadyForRecruitment(selectedClient)) {
      setFieldErrors({ client_id: 'Client onboarding is incomplete — finish compliance first.' });
      goStep(1); return;
    }
    setLoading(true);
    try {
      const created = await jobService.create({
        org_id: orgId, client_id: draft.client_id,
        title: draft.title.trim(),
        description: draft.description || null,
        career_seo_summary: null,
        jd_url: draft.jd_url || null,
        skills_required: draft.skills_required,
        experience_min: draft.experience_min ? parseInt(draft.experience_min, 10) : null,
        experience_max: draft.experience_max ? parseInt(draft.experience_max, 10) : null,
        location: draft.location || null,
        status: 'open',
        publish_status: draft.post_publish === 'pending_review' ? 'pending_review' : 'draft',
        published_at: null, publish_channels: [],
        scheduled_publish_at: null, scheduled_publish_job_id: null,
        opened_at: new Date().toISOString().split('T')[0],
        filled_at: null, created_by: userId,
      } as Parameters<typeof jobService.create>[0]);
      if (requisitionId) {
        try { await requisitionService.linkJob(requisitionId, created.id); } catch { /* non-fatal */ }
      }
      try { localStorage.setItem(WIZARD_HIDE_KEY, '0'); } catch { /* ignore */ }
      onCreated(created);
      onClose();
    } catch (e: unknown) {
      setFieldErrors({ _submit: e instanceof Error ? e.message : 'Failed to create job.' });
    } finally { setLoading(false); }
  };

  const meta = STEP_META[step];
  const selectedClientName = clients.find((c) => c.id === draft.client_id)?.name ?? '—';

  const nextLabel = () => {
    if (step === 1) return 'Description →';
    if (step === 2) return 'Skills →';
    if (step === 3) return 'Ops lifecycle →';
    if (step === 4) return 'Publish setup →';
    if (step === 5) return 'Review →';
    return '';
  };

  /* ── step body ── */
  const renderBody = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4 ggj-wizard-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Client<Req /></label>
                <select className={`input w-full mt-1 ${errCls('client_id', fieldErrors)}`}
                  value={draft.client_id} onChange={(e) => set('client_id', e.target.value)}>
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <FieldError msg={fieldErrors.client_id} />
              </div>
              <div>
                <label className="label">Job title<Req /></label>
                <input className={`input w-full mt-1 ${errCls('title', fieldErrors)}`}
                  placeholder="Senior Java Developer" value={draft.title}
                  onChange={(e) => set('title', e.target.value)} />
                <FieldError msg={fieldErrors.title} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Location</label>
                <input className="input w-full mt-1" placeholder="Bengaluru / Remote / Hybrid"
                  value={draft.location} onChange={(e) => set('location', e.target.value)} />
              </div>
              <div>
                <label className="label">Min exp (yrs)</label>
                <input type="number" min={0} className="input w-full mt-1"
                  value={draft.experience_min} onChange={(e) => set('experience_min', e.target.value)} />
              </div>
              <div>
                <label className="label">Max exp (yrs)</label>
                <input type="number" min={0} className="input w-full mt-1"
                  value={draft.experience_max} onChange={(e) => set('experience_max', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 ggj-wizard-form">
            <div>
              <label className="label">JD URL <span className="text-xs vscode-text-tertiary font-normal">(optional)</span></label>
              <input className="input w-full mt-1" placeholder="https://linkedin.com/jobs/…"
                value={draft.jd_url} onChange={(e) => set('jd_url', e.target.value)} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="label mb-0">Description</label>
                <AiAssistIconButton onClick={() => setJdAiOpen(true)} title="AI — generate JD" />
                <span className="text-xs vscode-text-tertiary">or AI-generate</span>
              </div>
              <textarea className="input w-full resize-none" rows={7}
                placeholder="Paste job description or use AI…"
                value={draft.description} onChange={(e) => set('description', e.target.value)} />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4 ggj-wizard-form">
            <p className="text-sm vscode-text-secondary">Required skills — power auto-matching and search filters.</p>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <input className="input w-full" placeholder="Type a skill and press Enter…"
                  value={skillInput} onChange={(e) => handleSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addSkill(); }
                    if (e.key === 'Escape') setShowSkillDrop(false);
                  }}
                  onBlur={() => setTimeout(() => setShowSkillDrop(false), 150)} autoComplete="off" />
                {showSkillDrop && (
                  <ul className="absolute z-20 mt-1 w-full rounded-lg border vscode-border shadow-xl max-h-44 overflow-y-auto"
                    style={{ background: 'var(--vscode-bg-secondary)' }}>
                    {skillSuggestions.filter((s) => !draft.skills_required.includes(s.name)).map((s) => (
                      <li key={s.id} className="px-3 py-2 text-sm cursor-pointer hover:bg-brand-500/10 vscode-text-primary"
                        onMouseDown={() => addSkill(s.name)}>{s.name}</li>
                    ))}
                    {skillInput.trim() && !skillSuggestions.find((s) => s.name.toLowerCase() === skillInput.trim().toLowerCase()) && (
                      <li className="px-3 py-2 text-sm cursor-pointer hover:bg-brand-500/10 text-brand-400 italic"
                        onMouseDown={() => addSkill(skillInput.trim())}>
                        Add &quot;{skillInput.trim()}&quot; as custom
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <button type="button" className="btn-secondary shrink-0" onClick={() => addSkill()}>Add</button>
            </div>
            {draft.skills_required.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {draft.skills_required.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium"
                    style={{ background: 'rgba(139,128,249,0.12)', color: '#8B80F9', border: '1px solid rgba(139,128,249,0.3)' }}>
                    {s}
                    <button type="button" onClick={() => removeSkill(s)} className="hover:text-red-400 ml-0.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm vscode-text-tertiary italic">No skills yet — you can add them later.</p>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="rounded-xl p-3.5 border vscode-border"
              style={{ background: 'color-mix(in srgb, #10b981 5%, var(--vscode-bg-secondary))' }}>
              <p className="text-sm font-semibold vscode-text-primary mb-1 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-400" /> Ops / pipeline lifecycle
              </p>
              <p className="text-xs vscode-text-secondary leading-relaxed">
                Jobs start at <strong>Recruiting</strong>. Move through these stages from the job detail page as the role progresses.
              </p>
            </div>
            <JobOpsFlowStepper orientation="horizontal" status="open"
              actionHint="New jobs start here — update status on the job detail page." />
            <div className="space-y-2">
              {JOB_OPS_FLOW_STEPS.map((s, i) => (
                <div key={s.key} className="flex gap-3 rounded-lg p-3 border vscode-border"
                  style={{ background: 'var(--vscode-bg-secondary)' }}>
                  <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-white mt-0.5"
                    style={{ background: '#10b981' }}>{i + 1}</span>
                  <div>
                    <p className="text-sm font-semibold vscode-text-primary">{s.title}</p>
                    <p className="text-xs vscode-text-secondary mt-0.5 leading-relaxed">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="rounded-xl p-3.5 border vscode-border"
              style={{ background: 'color-mix(in srgb, #8B80F9 5%, var(--vscode-bg-secondary))' }}>
              <p className="text-sm font-semibold vscode-text-primary mb-1 flex items-center gap-2">
                <Send className="w-4 h-4" style={{ color: '#8B80F9' }} /> Publish / careers lifecycle
              </p>
              <p className="text-xs vscode-text-secondary leading-relaxed">
                Controls external visibility. Jobs start as <strong>Draft</strong> until approved for listing.
              </p>
            </div>
            <JobPublishFlowStepper orientation="horizontal" publishStatus="draft" />
            <div className="space-y-2">
              {JOB_PUBLISH_FLOW_STEPS.map((s, i) => (
                <div key={s.key} className="flex gap-3 rounded-lg p-3 border vscode-border"
                  style={{ background: 'var(--vscode-bg-secondary)' }}>
                  <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-white mt-0.5"
                    style={{ background: '#8B80F9' }}>{i + 1}</span>
                  <div>
                    <p className="text-sm font-semibold vscode-text-primary">{s.title}</p>
                    <p className="text-xs vscode-text-secondary mt-0.5 leading-relaxed">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-4 border vscode-border space-y-3"
              style={{ background: 'var(--vscode-bg-secondary)' }}>
              <p className="text-sm font-semibold vscode-text-primary">Set initial status after creation</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="radio" className="mt-0.5" checked={draft.post_publish === 'draft'}
                  onChange={() => set('post_publish', 'draft')} />
                <span>
                  <strong className="text-sm vscode-text-primary">Draft</strong>
                  <span className="text-xs vscode-text-secondary block mt-0.5">Keep off careers until you manually publish later.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="radio" className="mt-0.5" checked={draft.post_publish === 'pending_review'}
                  onChange={() => set('post_publish', 'pending_review')} />
                <span>
                  <strong className="text-sm vscode-text-primary">Pending review</strong>
                  <span className="text-xs vscode-text-secondary block mt-0.5">Already emailed client — moves to approval stage.</span>
                </span>
              </label>
            </div>
          </div>
        );

      case 6: {
        const summaryGroups = [
          {
            label: 'Role basics', step: 1,
            rows: [
              { key: 'Title', value: draft.title, required: true },
              { key: 'Client', value: selectedClientName, required: true },
              { key: 'Location', value: draft.location },
              { key: 'Experience', value: [draft.experience_min && `${draft.experience_min}y min`, draft.experience_max && `${draft.experience_max}y max`].filter(Boolean).join(' / ') },
            ],
          },
          {
            label: 'Description', step: 2,
            rows: [
              { key: 'JD URL', value: draft.jd_url },
              { key: 'Description', value: draft.description ? `${draft.description.slice(0, 100)}${draft.description.length > 100 ? '…' : ''}` : '' },
            ],
          },
          {
            label: 'Skills', step: 3,
            rows: [{ key: 'Skills', value: draft.skills_required.join(', ') }],
          },
          {
            label: 'Publish', step: 5,
            rows: [{ key: 'Initial status', value: draft.post_publish === 'pending_review' ? 'Pending review' : 'Draft' }],
          },
        ];
        return (
          <div className="space-y-3">
            {fieldErrors._submit && (
              <div className="rounded-xl p-3 border border-red-500/40 bg-red-500/8 text-xs text-red-400">
                {fieldErrors._submit}
              </div>
            )}
            {summaryGroups.map((group) => (
              <div key={group.label} className="rounded-xl border vscode-border overflow-hidden"
                style={{ background: 'var(--vscode-bg-secondary)' }}>
                <div className="flex items-center justify-between px-4 py-2 border-b vscode-border"
                  style={{ background: 'color-mix(in srgb, #8B80F9 8%, var(--vscode-bg-tertiary))' }}>
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#8B80F9' }}>
                    {group.label}
                  </span>
                  <button type="button" onClick={() => goStep(group.step)}
                    className="text-xs font-semibold hover:underline" style={{ color: '#8B80F9' }}>
                    Edit →
                  </button>
                </div>
                <div className="divide-y vscode-border">
                  {group.rows.map((row) => {
                    const filled = !!row.value;
                    const missing = !filled && (row as { required?: boolean }).required;
                    return (
                      <div key={row.key} className="flex items-baseline gap-3 px-4 py-2">
                        <span className="text-xs vscode-text-tertiary shrink-0 w-24">{row.key}</span>
                        <span className="flex items-center gap-1.5 text-sm min-w-0">
                          {missing ? (
                            <span className="text-amber-400 font-medium text-xs">⚠ Required</span>
                          ) : filled ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                              <span className="vscode-text-primary truncate text-xs">{row.value}</span>
                            </>
                          ) : (
                            <span className="vscode-text-tertiary text-xs">—</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      }

      default: return null;
    }
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={prefillRequisition ? `Create job — ${prefillRequisition.title}` : 'New job'}
        icon={<Briefcase className="w-4 h-4" />}
        subtitle={prefillRequisition ? `Requisition · ${prefillRequisition.status?.replace(/_/g, ' ')} · Step ${step} of ${TOTAL}` : meta.subtitle}
        size="xl"
      >
        {/* 3-zone layout within modal */}
        <div className="flex flex-col" style={{ maxHeight: 'calc(85vh - 10rem)' }}>

          {/* ZONE 1 — stepper */}
          <div className="shrink-0 mb-4 pb-4 border-b vscode-border">
            {/* step context badge */}
            <div className="flex items-center gap-2 mb-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg text-white flex-shrink-0"
                style={{ background: '#8B80F9' }}>
                {meta.icon}
              </span>
              <p className="text-sm font-bold vscode-text-primary">{meta.title}</p>
            </div>
            <ClientOnboardingWizardStepper
              labels={STEP_LABELS}
              currentStep={step}
              maxReached={maxReached}
              onSelectStep={goStep}
            />
          </div>

          {/* ZONE 2 — scrollable content */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto pr-1 min-h-0">
            {renderBody()}
          </div>

          {/* ZONE 3 — nav buttons */}
          <div className="shrink-0 flex items-center justify-between gap-3 pt-4 border-t vscode-border mt-4">
            <button type="button" disabled={step === 1} onClick={back}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-30"
              style={{ color: '#8B80F9', border: '1.5px solid #8B80F9', background: 'transparent' }}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {step < TOTAL ? (
              <button type="button" onClick={next}
                className="ggj-wizard-btn-primary inline-flex items-center gap-1.5">
                {nextLabel()} <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={() => void submitJob()} disabled={loading}
                className="ggj-wizard-btn-primary inline-flex items-center gap-2">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                  : <><CheckCircle className="w-4 h-4" /> [Create job]</>
                }
              </button>
            )}
          </div>
        </div>
      </Modal>

      <ContextAiAssistModal
        open={jdAiOpen}
        onClose={() => setJdAiOpen(false)}
        orgId={orgId}
        feature="ai_assist_job_wizard_jd"
        preset="job_description"
        title="AI — job description"
        contextHint={`Title: ${draft.title}\nLocation: ${draft.location}\nSkills: ${draft.skills_required.join(', ')}`}
        original={draft.description}
        onApply={(text, mode) =>
          setDraft((d) => ({
            ...d,
            description:
              mode === 'replace'
                ? text
                : d.description.trim()
                  ? `${d.description.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    </>
  );
}

export function readJobWizardHidden(): boolean {
  if (typeof window === 'undefined') return false;
  try { return localStorage.getItem(WIZARD_HIDE_KEY) === '1'; } catch { return false; }
}
